import { useSnackbar } from 'notistack';
import { useState } from 'react';

import type { VeranstaltungRessourcenCalendarProps } from '../../components/general/VivaRessourcenCalendars/VeranstaltungRessourcenCalendar/VeranstaltungRessourcenCalendar';

import { VeranstaltungRessourcenCalendar } from '../../components/general';
import { type ExperteAntwort, type Leistung, type ThemaFromThemaRouter, type Veranstaltung } from '../../dtos';
import { queryClient, trpc } from '../../trpc';
import { getVeranstaltungStart, isOnlineVeranstaltung } from '../../utils';
import { useUpdateRessourcenBlockungenOfVeranstaltung } from './useUpdateRessourcenBlockungenOfVeranstaltung.ts';
import { DiscardAllVorschlaegeDialog } from './VorschlaegeVerwerfenDialog.tsx';

type UmplanenMode = 'alternativvorschlaegePruefen' | 'umplanen';

type VorschlaegePruefenProps = {
  readonly thema: Pick<
    ThemaFromThemaRouter,
    'id' | 'planungsinformation' | 'isOnline' | 'strukturellesFormat' | 'module' | 'manuellerAusschreibungsprozess'
  >;
  readonly veranstaltung: Pick<
    Veranstaltung,
    | 'ablauf'
    | 'akaModulId'
    | 'akaVeranstaltungId'
    | 'akaDurchgangId'
    | 'onlineTool'
    | 'experteBlockungen'
    | 'experteTermine'
    | 'raumBlockungen'
    | 'standortBlockungen'
    | 'teilleistungen'
    | 'capabilities'
    | 'manuellerAusschreibungsprozess'
    | 'vivaStatus'
  >;
  readonly leistungId: Leistung['leistungId'];
  readonly activeTeilleistungId: string;
  readonly modulLabel?: string;
  readonly onClose: () => void;
};

export const VorschlaegePruefen = ({
  thema,
  veranstaltung,
  leistungId,
  activeTeilleistungId,
  modulLabel,
  onClose,
}: VorschlaegePruefenProps) => {
  const leistung = veranstaltung.teilleistungen.find(
    (teilleistung) => teilleistung.leistung.leistungId === leistungId,
  )?.leistung;
  const experteAntworten = leistung?.ausschreibung?.experteAntworten ?? [];
  const [hadPersistedSelectionOnOpen] = useState(() =>
    hasPersistedSelectionForTeilleistung(experteAntworten, activeTeilleistungId),
  );
  const [umplanenMode, setUmplanenMode] = useState<UmplanenMode>('alternativvorschlaegePruefen');
  const [discardDialogOpen, setDiscardDialogOpen] = useState(false);
  const [selectedVorschlagKey, setSelectedVorschlagKey] = useState<string | undefined>(() =>
    experteAntworten
      .flatMap((antwort) => antwort.alternativvorschlaege)
      .find((vorschlag) => vorschlag.teilleistungId === activeTeilleistungId && vorschlag.isSelected)
      ?.id.toString(),
  );
  const { enqueueSnackbar } = useSnackbar();
  const { save: saveRessourcenBlockungen } = useUpdateRessourcenBlockungenOfVeranstaltung(veranstaltung);
  const acceptMutation = trpc.veranstaltung.selectAlternativvorschlag.useMutation({
    onError: (error) => {
      enqueueSnackbar(error.message, {
        variant: 'error',
        persist: true,
      });
    },
    onSuccess: async () => {
      await queryClient.refetchQueries();
    },
  });
  const deselectMutation = trpc.veranstaltung.deselectAlternativvorschlag.useMutation({
    onError: (error) => {
      enqueueSnackbar(error.message, {
        variant: 'error',
        persist: true,
      });
    },
    onSuccess: async () => {
      await queryClient.refetchQueries();
    },
  });
  const verwerfenMutation = trpc.leistung.discardAllAlternativvorschlaege.useMutation({
    onError: (error) => {
      enqueueSnackbar(error.message, {
        variant: 'error',
        persist: true,
      });
    },
    onSuccess: async (result) => {
      if (result.errorMessages.length === 0) {
        await queryClient.refetchQueries();
      }
    },
  });
  const selectedVorschlag = findSelectedVorschlag(experteAntworten, activeTeilleistungId, selectedVorschlagKey);
  const hasPersistedSelection = hasPersistedSelectionForTeilleistung(experteAntworten, activeTeilleistungId);

  const handleAuswahlAufheben = async (): Promise<void> => {
    if (!selectedVorschlag) {
      return;
    }

    try {
      await deselectMutation.mutateAsync({
        akaVeranstaltungId: veranstaltung.akaVeranstaltungId,
        alternativvorschlagDbId: selectedVorschlag.alternativvorschlagDbId,
        isOnlineVeranstaltung: isOnlineVeranstaltung(veranstaltung),
        blockungen: [],
      });
    } catch {
      return;
    }

    enqueueSnackbar('Auswahl aufgehoben', { variant: 'success' });
    onClose();
  };

  const handleAcceptVorschlag: VeranstaltungRessourcenCalendarProps['onSave'] = async (blockungen) => {
    if (!selectedVorschlag) {
      return;
    }

    try {
      await acceptMutation.mutateAsync({
        akaVeranstaltungId: veranstaltung.akaVeranstaltungId,
        alternativvorschlagDbId: selectedVorschlag.alternativvorschlagDbId,
        isOnlineVeranstaltung: isOnlineVeranstaltung(veranstaltung),
        blockungen,
      });
    } catch {
      return;
    }

    enqueueSnackbar('Vorschlag übernommen', { variant: 'success' });
    onClose();
  };

  const handleUmplanenSave: VeranstaltungRessourcenCalendarProps['onSave'] = async (blockungen, ablauf) => {
    try {
      await saveRessourcenBlockungen(blockungen, ablauf);
    } catch {
      return;
    }

    onClose();
  };

  const handleDiscardVorschlaege = async (): Promise<void> => {
    let result;
    try {
      result = await verwerfenMutation.mutateAsync({ leistungId });
    } catch {
      return;
    }

    if (result.errorMessages.length > 0) {
      result.errorMessages.forEach((message) => {
        enqueueSnackbar(message, { variant: 'error', persist: true });
      });
      return;
    }

    result.successMessages.forEach((message) => {
      enqueueSnackbar(message, { variant: 'success' });
    });
    setDiscardDialogOpen(false);
    setUmplanenMode('umplanen');
  };

  const isAlternativvorschlaegePruefen = umplanenMode === 'alternativvorschlaegePruefen';
  const calendarOnSave = isAlternativvorschlaegePruefen ? handleAcceptVorschlag : handleUmplanenSave;

  return (
    <>
      <VeranstaltungRessourcenCalendar
        key={umplanenMode}
        thema={thema}
        veranstaltung={veranstaltung}
        isFullscreen
        initDate={getVeranstaltungStart(veranstaltung)}
        onSave={calendarOnSave}
        onCancel={onClose}
        isVorschlaegePruefenMode={isAlternativvorschlaegePruefen}
        vorschlaegePruefenConfig={
          isAlternativvorschlaegePruefen
            ? {
                modulLabel,
                leistungId,
                activeTeilleistungId,
                selectedVorschlagKey,
                onVorschlagChange: setSelectedVorschlagKey,
                selectedVorschlag,
                weitereExperteSapIds:
                  selectedVorschlag === undefined
                    ? []
                    : (leistung?.ausschreibung?.experteAntworten
                        .map((antwort) => antwort.experte.experteSapId)
                        .filter((experteSapId) => experteSapId !== selectedVorschlag.experteSapId) ?? []),
                isUebernehmenPending: acceptMutation.isPending,
                isVerwerfenPending: verwerfenMutation.isPending,
                isVerwerfenDialogOpen: discardDialogOpen,
                canAuswahlAufheben: hadPersistedSelectionOnOpen && hasPersistedSelection,
                isAuswahlAufhebenPending: deselectMutation.isPending,
                onAuswahlAufheben: handleAuswahlAufheben,
                onDiscardAllVorschlaege: () => {
                  setDiscardDialogOpen(true);
                },
              }
            : undefined
        }
      />
      {isAlternativvorschlaegePruefen && (
        <DiscardAllVorschlaegeDialog
          open={discardDialogOpen}
          loading={verwerfenMutation.isPending}
          onApprove={handleDiscardVorschlaege}
          onClose={() => {
            setDiscardDialogOpen(false);
          }}
        />
      )}
    </>
  );
};

function hasPersistedSelectionForTeilleistung(
  experteAntworten: readonly ExperteAntwort[],
  teilleistungId: string,
): boolean {
  return experteAntworten.some((antwort) =>
    antwort.alternativvorschlaege.some(
      (vorschlag) => vorschlag.teilleistungId === teilleistungId && vorschlag.isSelected,
    ),
  );
}

function findSelectedVorschlag(
  experteAntworten: readonly ExperteAntwort[],
  teilleistungId: string,
  selectedVorschlagKey: string | undefined,
) {
  if (selectedVorschlagKey === undefined) {
    return undefined;
  }

  const alternativvorschlagDbId = Number(selectedVorschlagKey);
  const experteAntwort = experteAntworten.find((antwort) =>
    antwort.alternativvorschlaege.some(
      (vorschlag) => vorschlag.id === alternativvorschlagDbId && vorschlag.teilleistungId === teilleistungId,
    ),
  );
  const vorschlag = experteAntwort?.alternativvorschlaege.find(
    (alternativvorschlag) =>
      alternativvorschlag.id === alternativvorschlagDbId &&
      alternativvorschlag.teilleistungId === teilleistungId &&
      alternativvorschlag.leistungszeitraeume.length > 0,
  );

  if (!vorschlag || !experteAntwort) {
    return undefined;
  }

  return {
    alternativvorschlagDbId: vorschlag.id,
    experteSapId: experteAntwort.experte.experteSapId,
    leistungszeitraeume: vorschlag.leistungszeitraeume,
  };
}
